import { forwardRef, type ReactNode } from 'react';
import MuiToggleButton from '@mui/material/ToggleButton';
import MuiToggleButtonGroup from '@mui/material/ToggleButtonGroup';

export type ToggleButtonOption<T extends string> = {
  value: T;
  /** Icon (or short label) inside the button. */
  icon: ReactNode;
  'aria-label': string;
};

export type ToggleButtonGroupProps<T extends string> = {
  value: T;
  /** Called with the newly selected value (clicking the active button keeps it selected). */
  onChange: (value: T) => void;
  options: ToggleButtonOption<T>[];
  'aria-label'?: string;
};

/**
 * Exclusive toggle (e.g. folders / table view): grey track with padding around it,
 * the active button is primary with a white icon. Styles live in `ToggleButtonGroup.theme.tsx`.
 */
function ToggleButtonGroupInner<T extends string>(
  { value, onChange, options, 'aria-label': ariaLabel }: ToggleButtonGroupProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  return (
    <MuiToggleButtonGroup
      ref={ref}
      exclusive
      value={value}
      onChange={(_event, next: T | null) => {
        if (next) onChange(next);
      }}
      aria-label={ariaLabel}
      className="SmToggleButtonGroup-root"
    >
      {options.map(option => (
        <MuiToggleButton
          key={option.value}
          value={option.value}
          aria-label={option['aria-label']}
          className="SmToggleButton-root"
          disableRipple
        >
          {option.icon}
        </MuiToggleButton>
      ))}
    </MuiToggleButtonGroup>
  );
}

export const ToggleButtonGroup = forwardRef(ToggleButtonGroupInner) as <T extends string>(
  props: ToggleButtonGroupProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => ReturnType<typeof ToggleButtonGroupInner>;
